import type { ReactNode } from 'react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  icon: ReactNode;
  title: string;
  body: string;
  primary?: EmptyStateAction;
  secondary?: EmptyStateAction;
  /** Terceira saída, discreta (ghost). */
  tertiary?: EmptyStateAction;
  /** Root testid; as ações derivam `${testId}-primary|-secondary|-tertiary`. */
  testId: string;
  /** Tom do círculo do ícone (Figma `Empty` Status=Neutral → `neutral`). */
  tone?: 'neutral' | 'destructive';
  /** `outline` p/ estados cujo único CTA é de contorno. */
  primaryVariant?: 'default' | 'outline';
  /**
   * `lg` (default): botões grandes em qualquer largura.
   * `responsive`: tamanho padrão no mobile e grande a partir de `sm` (Figma
   * `Empty` — `Size=Default` empilhado no mobile, `Size=Large` lado a lado no desktop).
   */
  buttonSize?: 'lg' | 'responsive';
  className?: string;
}

/**
 * Estado vazio / informativo (Figma `Empty`): ícone em círculo, título,
 * descrição e até três ações — empilhadas no mobile e lado a lado a partir de
 * `sm`. Ocupa só o container em que é montado; o `ErrorScreen` é quem o
 * envolve em `min-h-dvh` para as telas globais de erro.
 */
export function EmptyState({
  icon,
  title,
  body,
  primary,
  secondary,
  tertiary,
  testId,
  tone = 'neutral',
  primaryVariant = 'default',
  buttonSize = 'lg',
  className,
}: EmptyStateProps) {
  const sizeClass = buttonSize === 'responsive' ? 'h-9 px-4 sm:h-10 sm:px-8' : 'sm:px-8';
  const buttonClass = cn('w-full sm:w-auto', sizeClass);
  const hasActions = Boolean(primary || secondary || tertiary);
  return (
    <div
      data-testid={testId}
      className={cn('flex w-full max-w-md flex-col items-center gap-6 text-center', className)}
    >
      <div
        data-testid={`${testId}-icon`}
        className={cn(
          'flex size-16 shrink-0 items-center justify-center rounded-full',
          tone === 'destructive' ? 'bg-destructive/10' : 'bg-muted'
        )}
      >
        {icon}
      </div>

      <div className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-semibold leading-none tracking-[-0.6px] text-foreground">
          {title}
        </h1>
        <p className="text-sm leading-5 text-accent-foreground">{body}</p>
      </div>

      {hasActions ? (
        <div
          data-testid={`${testId}-actions`}
          className="flex w-full flex-col gap-3 sm:flex-row sm:justify-center"
        >
          {primary ? (
            <Button
              type="button"
              size="lg"
              variant={primaryVariant}
              className={buttonClass}
              data-testid={`${testId}-primary`}
              onClick={primary.onClick}
            >
              {primary.label}
            </Button>
          ) : null}
          {secondary ? (
            <Button
              type="button"
              variant="outline"
              size="lg"
              className={buttonClass}
              data-testid={`${testId}-secondary`}
              onClick={secondary.onClick}
            >
              {secondary.label}
            </Button>
          ) : null}
          {tertiary ? (
            <Button
              type="button"
              variant="ghost"
              size="lg"
              className={buttonClass}
              data-testid={`${testId}-tertiary`}
              onClick={tertiary.onClick}
            >
              {tertiary.label}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
